import { Property } from '../types';
import { BackendFilterPayload, PropertySearchResult, MapPin, PropertyFacets, PaginatedResult } from '../server/properties/propertyTypes';
import { getClientAuthHeaders } from './clientAuthHeaders';

const BASE_URL = '/api/properties';

export async function searchPropertiesApi(filters: BackendFilterPayload): Promise<PropertySearchResult> {
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/filter`, { method: 'POST', headers, body: JSON.stringify(filters) });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error en búsqueda de inmuebles');
  return data;
}

export async function getMapPinsApi(filters?: BackendFilterPayload): Promise<MapPin[]> {
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/map/pins`, { method: 'POST', headers, body: JSON.stringify(filters || {}) });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error obteniendo pines del mapa');
  return data.pins || [];
}

export async function getClusterPropertiesApi(ids: string[], page = 1, limit = 5): Promise<PaginatedResult<Property>> {
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/map/cluster-items`, { method: 'POST', headers, body: JSON.stringify({ ids, page, limit }) });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error obteniendo inmuebles del cluster');
  return data;
}

export async function searchByCodesApi(
  codes: string[] | string,
  options?: { isPublic?: boolean; role?: string }
): Promise<{ items: Property[]; total: number; notFoundCodes: string[] }> {
  const headers = await getClientAuthHeaders();
  const body = {
    codes,
    isPublic: options?.isPublic,
    role: options?.role,
  };
  const res = await fetch(`${BASE_URL}/codes/by-codes`, { method: 'POST', headers, body: JSON.stringify(body) });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error buscando por códigos');
  return data;
}

export async function getCodeSuggestionsApi(query: string): Promise<Array<{ id: string; codigo: string; ciudad: string; tipo: string; precio: number }>> {
  if (!query || query.length < 2) return [];
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/codes/code-autocomplete?q=${encodeURIComponent(query)}`, { headers });
  const data = await res.json();
  return data.suggestions || [];
}

export async function getPropertyFacetsApi(): Promise<PropertyFacets> {
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/facets`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error obteniendo facetas de filtros');
  return data.facets;
}

export async function getPropertyByIdApi(id: string, collectionName?: string): Promise<Property> {
  const headers = await getClientAuthHeaders();
  const query = collectionName ? `?collection=${encodeURIComponent(collectionName)}` : '';
  const res = await fetch(`${BASE_URL}/${encodeURIComponent(id)}${query}`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Inmueble no encontrado');
  return data.property;
}

export async function getPropertiesByIdsApi(
  ids: string[],
  options?: { isPublic?: boolean; role?: string; collectionHint?: string }
): Promise<Property[]> {
  if (!ids || ids.length === 0) return [];
  const headers = await getClientAuthHeaders();
  const res = await fetch(`${BASE_URL}/codes/by-ids`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ids,
      pageSize: Math.max(ids.length, 100),
      isPublic: options?.isPublic ?? true,
      role: options?.role,
      collectionHint: options?.collectionHint,
    }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error obteniendo inmuebles');
  return data.items || [];
}

export async function getNewlyAddedPropertiesApi(dateStr?: string | null): Promise<Property[]> {
  const headers = await getClientAuthHeaders();
  const queryParam = dateStr ? `?dateStr=${encodeURIComponent(dateStr)}` : '';
  const res = await fetch(`${BASE_URL}/newly-added${queryParam}`, { headers });
  const data = await res.json();
  if (!data.success) throw new Error(data.message || 'Error obteniendo inmuebles recientes');
  return data.items || [];
}
